import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { getComicById, categories } from '@/mocks/comics';

const ArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 no-underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comic = getComicById(article.comicId);
  const relatedArticles = getRelatedArticles(article);
  const category = categories.find((c) => c.slug === article.category);

  const pageTitle = `${article.title}_韩国漫画推荐_韩漫在线免费阅读-韩漫屋`;
  const pageDesc = article.summary;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    url: `https://hanmanwu.com/article/${id}`,
    articleBody: article.sections.map((s) => s.content).join(' '),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanwu.com/article/${id}`,
    },
  };

  return (
    <>
      <SEOHelmet
        title={pageTitle}
        description={pageDesc}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://hanmanwu.com/article/${id}`}
        ogType="article"
        schema={schema}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            <div className="py-8 md:py-12">
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.publishDate}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.views.toLocaleString()} 阅读</span>
                {comic && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 rounded flex items-center justify-center bg-primary-500">
                        <span className="text-[10px] font-bold text-background-50 dark:text-foreground-950">{comic.rating}</span>
                      </span>
                      评分
                    </span>
                  </>
                )}
              </div>

              {comic && (
                <div className="mt-6 flex flex-col sm:flex-row gap-6 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-foreground-950 font-heading">{comic.title}</h3>
                    <p className="text-sm text-foreground-600 mt-0.5">{comic.author}</p>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-foreground-500">
                      <span>状态: <strong className="text-foreground-700">{comic.status}</strong></span>
                      <span>章节: <strong className="text-foreground-700">{comic.chapters}话</strong></span>
                      <span>类型: <strong className="text-foreground-700">{category?.name || article.category}</strong></span>
                    </div>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="prose-custom pb-10">
              {article.sections.map((section, idx) => {
                if (section.type === 'heading') {
                  return (
                    <h2 key={idx} className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mt-10 mb-4">
                      {section.content}
                    </h2>
                  );
                }
                if (section.type === 'paragraph') {
                  return (
                    <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  );
                }
                if (section.type === 'list') {
                  return (
                    <ul key={idx} className="list-disc pl-5 space-y-2 mb-4">
                      {section.items?.map((item, i) => (
                        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            {relatedArticles.length > 0 && (
              <section className="py-10 border-t border-background-200/70">
                <h3 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.slice(0, 3).map((ra) => {
                    const rc = getComicById(ra.comicId);
                    return rc ? (
                      <ComicCard key={ra.id} comic={rc} size="small" />
                    ) : null;
                  })}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default ArticlePage;